import {
  EmailAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  type OAuthCredential,
  type ActionCodeSettings,
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile as updateFirebaseProfile,
  type User,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Platform } from 'react-native';

import {
  authActionUrlWithEmailVerificationFlow,
  type EmailVerificationFlow,
} from '@/src/features/auth/auth-action-routing';
import { auth } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import { isAtLeastAge } from '@/src/utils/age';
import { UserFacingError } from '@/src/utils/errors';

type EmailPasswordInput = {
  email: string;
  password: string;
};

type SignUpInput = EmailPasswordInput & {
  acceptedLegal: boolean;
  dateOfBirth: string;
  fullName: string;
  inviteCode?: string | null;
};

type FacebookSignUpInput = SignUpInput & {
  accessToken: string;
};

export type GoogleCredentialTokens = {
  accessToken?: string | null;
  idToken?: string | null;
};

export type GooglePrefillProfile = GoogleCredentialTokens & {
  email?: string;
  fullName?: string;
  photoURL?: string;
};

type GoogleSignUpInput = SignUpInput & {
  tokens: GoogleCredentialTokens;
};

type SyncFacebookIdentityResult = {
  appScopedUserId: string;
};

type SyncCurrentAuthEmailResult = {
  email: string;
};

type CompleteRegistrationProfileInput = {
  acceptedLegal: boolean;
  dateOfBirth: string;
  email: string;
  fullName: string;
  initialPhotoURL?: string | null;
  inviteCode?: string | null;
  metaAttribution?: MetaRegistrationAttribution | null;
  username: string;
};

type CompleteRegistrationProfileResult = {
  ok: true;
  username: string;
};

type MetaRegistrationAttribution = {
  eventId: string;
  eventSourceUrl: string;
  fbc?: string | null;
  fbp?: string | null;
  method: 'facebook' | 'google' | 'password';
};

export type FacebookSignInResult = {
  isNewUser: boolean;
  user: User;
};

export type GoogleSignInResult = {
  isNewUser: boolean;
  user: User;
};

const facebookSignUpRequiredMessage =
  'Create your account with email and password before continuing with Facebook.';
const googleSignUpRequiredMessage =
  'Create your account with email and password before continuing with Google.';

const syncFacebookIdentityCallable = httpsCallable<
  Record<string, never>,
  SyncFacebookIdentityResult
>(functions, 'syncFacebookIdentity');
const syncCurrentAuthEmailCallable = httpsCallable<
  Record<string, never>,
  SyncCurrentAuthEmailResult
>(functions, 'syncCurrentAuthEmail');
const completeRegistrationProfileCallable = httpsCallable<
  CompleteRegistrationProfileInput,
  CompleteRegistrationProfileResult
>(functions, 'completeRegistrationProfile');

let lastRegistrationMetaEventId: string | null = null;

const firebaseAuthActionURL = 'https://frienzzi.com/auth/action';

function authActionCodeSettings({
  emailVerificationFlow,
}: { emailVerificationFlow?: EmailVerificationFlow } = {}): ActionCodeSettings {
  return {
    handleCodeInApp: false,
    url: emailVerificationFlow
      ? authActionUrlWithEmailVerificationFlow(
          firebaseAuthActionURL,
          emailVerificationFlow,
        )
      : firebaseAuthActionURL,
  };
}

export function isFacebookSignUpRequiredError(error: unknown) {
  return (
    error instanceof Error && error.message === facebookSignUpRequiredMessage
  );
}

export class GoogleSignUpRequiredError extends UserFacingError {
  readonly prefillProfile: GooglePrefillProfile | null;

  constructor(prefillProfile: GooglePrefillProfile | null = null) {
    super(googleSignUpRequiredMessage);
    this.prefillProfile = prefillProfile;
  }
}

export function isGoogleSignUpRequiredError(error: unknown) {
  return (
    error instanceof Error && error.message === googleSignUpRequiredMessage
  );
}

export function getGoogleSignUpPrefillProfile(error: unknown) {
  return error instanceof GoogleSignUpRequiredError
    ? error.prefillProfile
    : null;
}

function friendlyFacebookAuthError(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error.code === 'auth/account-exists-with-different-credential' ||
      error.code === 'auth/email-already-in-use' ||
      error.code === 'auth/credential-already-in-use')
  ) {
    return new UserFacingError(
      'An account already exists. Sign in first, then connect Facebook in Settings.',
    );
  }

  if (isFacebookSignUpRequiredError(error)) {
    return new UserFacingError(facebookSignUpRequiredMessage);
  }

  return error;
}

function friendlyGoogleAuthError(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error.code === 'auth/account-exists-with-different-credential' ||
      error.code === 'auth/email-already-in-use' ||
      error.code === 'auth/credential-already-in-use')
  ) {
    return new UserFacingError(
      'An account already exists. Sign in first, then connect Google in Settings.',
    );
  }

  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === 'auth/operation-not-allowed'
  ) {
    return new UserFacingError(
      'Google sign-in is not enabled for Frienzzi yet.',
    );
  }

  if (isGoogleSignUpRequiredError(error)) {
    return error;
  }

  return error;
}

function friendlyPasswordLinkError(error: unknown) {
  return friendlyFacebookAuthError(error);
}

function browserCookieValue(name: string) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
}

function newMetaRegistrationEventId() {
  if (
    Platform.OS === 'web' &&
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function browserFbcValue() {
  const existingFbc = browserCookieValue('_fbc');

  if (existingFbc) {
    return existingFbc;
  }

  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  const fbclid = new URL(window.location.href).searchParams.get('fbclid');

  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null;
}

function metaRegistrationAttribution(
  method: MetaRegistrationAttribution['method'],
): MetaRegistrationAttribution | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  return {
    eventId: newMetaRegistrationEventId(),
    eventSourceUrl: window.location.href,
    fbc: browserFbcValue(),
    fbp: browserCookieValue('_fbp'),
    method,
  };
}

export function consumeRegistrationMetaEventId() {
  const eventId = lastRegistrationMetaEventId;

  lastRegistrationMetaEventId = null;

  return eventId;
}

function assertValidSignUpInput({
  acceptedLegal,
  dateOfBirth,
}: Pick<SignUpInput, 'acceptedLegal' | 'dateOfBirth'>) {
  if (!acceptedLegal) {
    throw new Error(
      'You must agree to the Terms of Service and Privacy Policy.',
    );
  }

  if (!isAtLeastAge(dateOfBirth)) {
    throw new Error(
      'You must be at least 13 years old to create a Frienzzi account.',
    );
  }
}

async function completeRegistrationProfile({
  dateOfBirth,
  email,
  fullName,
  initialPhotoURL,
  inviteCode,
  metaMethod,
  username,
  user,
}: {
  dateOfBirth: string;
  email: string;
  fullName: string;
  initialPhotoURL?: string | null;
  inviteCode?: string | null;
  metaMethod: MetaRegistrationAttribution['method'];
  username: string;
  user: User;
}) {
  const normalizedFullName = fullName.trim();
  const photoURL =
    initialPhotoURL === undefined ? (user.photoURL ?? null) : initialPhotoURL;
  const metaAttribution = metaRegistrationAttribution(metaMethod);

  lastRegistrationMetaEventId = metaAttribution?.eventId ?? null;

  await updateFirebaseProfile(user, { displayName: normalizedFullName });
  await completeRegistrationProfileCallable({
    acceptedLegal: true,
    dateOfBirth,
    email,
    fullName: normalizedFullName,
    initialPhotoURL: photoURL,
    inviteCode: inviteCode ?? null,
    metaAttribution,
    username,
  });

  if (
    user.providerData.some((provider) => provider.providerId === 'facebook.com')
  ) {
    try {
      await syncFacebookIdentityCallable({});
    } catch {
      console.warn('Failed to sync Facebook identity after registration.');
    }
  }
}

function googleCredentialFromTokens(tokens: GoogleCredentialTokens) {
  if (!tokens.idToken && !tokens.accessToken) {
    throw new Error('Google did not return a sign-in token.');
  }

  return GoogleAuthProvider.credential(
    tokens.idToken ?? null,
    tokens.accessToken ?? null,
  );
}

function googleTokensFromCredential(
  credential: OAuthCredential | null,
): GoogleCredentialTokens {
  return {
    accessToken: credential?.accessToken ?? null,
    idToken: credential?.idToken ?? null,
  };
}

function googlePrefillProfileFromUser(
  user: User,
  tokens: GoogleCredentialTokens,
): GooglePrefillProfile {
  return {
    accessToken: tokens.accessToken ?? null,
    email: user.email?.trim() || undefined,
    fullName: user.displayName?.trim() || undefined,
    idToken: tokens.idToken ?? null,
    photoURL: user.photoURL?.trim() || undefined,
  };
}

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

async function signInWithGooglePopup(): Promise<GoogleSignInResult> {
  if (Platform.OS !== 'web') {
    throw new UserFacingError(
      'Google sign-in needs app client IDs before it can run here.',
    );
  }

  const credential = await signInWithPopup(auth, createGoogleProvider());
  const info = getAdditionalUserInfo(credential);

  if (info?.isNewUser) {
    const googleCredential =
      GoogleAuthProvider.credentialFromResult(credential);
    const prefillProfile = googlePrefillProfileFromUser(
      credential.user,
      googleTokensFromCredential(googleCredential),
    );

    try {
      await deleteUser(credential.user);
    } catch {
      await firebaseSignOut(auth);
    }

    throw new GoogleSignUpRequiredError(prefillProfile);
  }

  return {
    isNewUser: Boolean(info?.isNewUser),
    user: credential.user,
  };
}

export async function signInWithEmail({
  email,
  password,
}: EmailPasswordInput): Promise<User> {
  const credential = await signInWithEmailAndPassword(
    auth,
    email.trim(),
    password,
  );
  return credential.user;
}

export async function signUpWithEmail({
  acceptedLegal,
  dateOfBirth,
  email,
  password,
}: SignUpInput): Promise<User> {
  assertValidSignUpInput({ acceptedLegal, dateOfBirth });

  const credential = await createUserWithEmailAndPassword(
    auth,
    email.trim(),
    password,
  );
  const { user } = credential;

  try {
    await sendEmailVerification(
      user,
      authActionCodeSettings({ emailVerificationFlow: 'signup' }),
    );
  } catch (error) {
    try {
      await deleteUser(user);
    } catch {
      await firebaseSignOut(auth);
    }

    throw error;
  }

  return user;
}

export async function signUpWithEmailAndFacebook({
  acceptedLegal,
  accessToken,
  dateOfBirth,
  email,
  password,
}: FacebookSignUpInput): Promise<User> {
  assertValidSignUpInput({ acceptedLegal, dateOfBirth });

  let credential;

  try {
    credential = await createUserWithEmailAndPassword(
      auth,
      email.trim(),
      password,
    );
  } catch (error) {
    throw friendlyFacebookAuthError(error);
  }

  const { user } = credential;
  let shouldDeleteCreatedUser = true;

  try {
    const facebookCredential = FacebookAuthProvider.credential(accessToken);
    const linkedCredential = await linkWithCredential(user, facebookCredential);

    await sendEmailVerification(
      linkedCredential.user,
      authActionCodeSettings({ emailVerificationFlow: 'signup' }),
    );

    shouldDeleteCreatedUser = false;

    return linkedCredential.user;
  } catch (error) {
    if (shouldDeleteCreatedUser) {
      try {
        await deleteUser(user);
      } catch {
        await firebaseSignOut(auth);
      }
    }

    throw friendlyFacebookAuthError(error);
  }
}

export async function signUpWithEmailAndGoogle({
  acceptedLegal,
  dateOfBirth,
  email,
  password,
  tokens,
}: GoogleSignUpInput): Promise<User> {
  assertValidSignUpInput({ acceptedLegal, dateOfBirth });

  let credential;

  try {
    credential = await createUserWithEmailAndPassword(
      auth,
      email.trim(),
      password,
    );
  } catch (error) {
    throw friendlyGoogleAuthError(error);
  }

  const { user } = credential;
  let shouldDeleteCreatedUser = true;

  try {
    const googleCredential = googleCredentialFromTokens(tokens);
    const linkedCredential = await linkWithCredential(user, googleCredential);

    if (!linkedCredential.user.emailVerified) {
      await sendEmailVerification(
        linkedCredential.user,
        authActionCodeSettings({ emailVerificationFlow: 'signup' }),
      );
    }

    shouldDeleteCreatedUser = false;
    return linkedCredential.user;
  } catch (error) {
    if (shouldDeleteCreatedUser) {
      try {
        await deleteUser(user);
      } catch {
        await firebaseSignOut(auth);
      }
    }

    throw friendlyGoogleAuthError(error);
  }
}

export async function signInWithFacebookAccessToken(
  accessToken: string,
): Promise<FacebookSignInResult> {
  try {
    const credential = FacebookAuthProvider.credential(accessToken);
    const userCredential = await signInWithCredential(auth, credential);
    const info = getAdditionalUserInfo(userCredential);

    if (info?.isNewUser) {
      try {
        await deleteUser(userCredential.user);
      } catch {
        await firebaseSignOut(auth);
      }

      throw new UserFacingError(facebookSignUpRequiredMessage);
    }

    await syncFacebookIdentityCallable({});
    return {
      isNewUser: Boolean(info?.isNewUser),
      user: userCredential.user,
    };
  } catch (error) {
    throw friendlyFacebookAuthError(error);
  }
}

export async function signInWithGoogleTokens(
  tokens?: GoogleCredentialTokens | null,
): Promise<GoogleSignInResult> {
  try {
    if (!tokens) {
      return await signInWithGooglePopup();
    }

    const credential = googleCredentialFromTokens(tokens);
    const userCredential = await signInWithCredential(auth, credential);
    const info = getAdditionalUserInfo(userCredential);

    if (info?.isNewUser) {
      const prefillProfile = googlePrefillProfileFromUser(
        userCredential.user,
        tokens,
      );

      try {
        await deleteUser(userCredential.user);
      } catch {
        await firebaseSignOut(auth);
      }

      throw new GoogleSignUpRequiredError(prefillProfile);
    }

    return {
      isNewUser: Boolean(info?.isNewUser),
      user: userCredential.user,
    };
  } catch (error) {
    throw friendlyGoogleAuthError(error);
  }
}

export async function linkCurrentUserWithFacebookAccessToken(
  accessToken: string,
): Promise<User> {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to connect Facebook.');
  }

  try {
    const credential = FacebookAuthProvider.credential(accessToken);
    const userCredential = await linkWithCredential(
      auth.currentUser,
      credential,
    );
    await syncFacebookIdentityCallable({});
    return userCredential.user;
  } catch (error) {
    throw friendlyFacebookAuthError(error);
  }
}

export async function linkCurrentUserWithGoogleTokens(
  tokens?: GoogleCredentialTokens | null,
): Promise<User> {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to connect Google.');
  }

  try {
    if (!tokens) {
      if (Platform.OS !== 'web') {
        throw new UserFacingError(
          'Google sign-in needs app client IDs before it can run here.',
        );
      }

      const credential = await linkWithPopup(
        auth.currentUser,
        createGoogleProvider(),
      );
      return credential.user;
    }

    const credential = googleCredentialFromTokens(tokens);
    const userCredential = await linkWithCredential(
      auth.currentUser,
      credential,
    );
    return userCredential.user;
  } catch (error) {
    throw friendlyGoogleAuthError(error);
  }
}

export async function setCurrentUserPassword(
  password: string,
  email?: string,
): Promise<User> {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('You must be signed in to set a password.');
  }

  const normalizedEmail = email?.trim() ?? currentUser.email ?? '';

  if (!normalizedEmail) {
    throw new Error(
      'Your account needs an email address before adding a password.',
    );
  }

  const hasPasswordProvider = currentUser.providerData.some(
    (provider) => provider.providerId === 'password',
  );

  if (hasPasswordProvider) {
    return auth.currentUser ?? currentUser;
  }

  let userCredential;

  try {
    const credential = EmailAuthProvider.credential(normalizedEmail, password);
    userCredential = await linkWithCredential(
      auth.currentUser ?? currentUser,
      credential,
    );
    await userCredential.user.getIdToken(true);
    await userCredential.user.reload();
  } catch (error) {
    throw friendlyPasswordLinkError(error);
  }

  return auth.currentUser ?? userCredential.user;
}

export async function completeCurrentRegistrationProfile({
  dateOfBirth,
  email,
  fullName,
  initialPhotoURL,
  inviteCode,
  metaMethod = 'password',
  username,
}: Omit<CompleteRegistrationProfileInput, 'acceptedLegal'> & {
  metaMethod?: MetaRegistrationAttribution['method'];
}): Promise<User> {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('You must be signed in to finish account setup.');
  }

  await completeRegistrationProfile({
    dateOfBirth,
    email,
    fullName,
    initialPhotoURL,
    inviteCode,
    metaMethod,
    username,
    user: currentUser,
  });

  await currentUser.getIdToken(true);
  await currentUser.reload();

  return auth.currentUser ?? currentUser;
}

export async function sendCurrentUserEmailVerification(
  flow: EmailVerificationFlow = 'signup',
) {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('You must be signed in to verify your email.');
  }

  if (!currentUser.email) {
    throw new Error('Your account does not have an email address.');
  }

  await sendEmailVerification(
    currentUser,
    authActionCodeSettings({ emailVerificationFlow: flow }),
  );
}

export async function refreshCurrentUser() {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    return null;
  }

  await currentUser.reload();
  await currentUser.getIdToken(true);

  return auth.currentUser ?? currentUser;
}

export async function requestCurrentUserEmailChange({
  currentPassword,
  emailVerificationFlow,
  newEmail,
}: {
  currentPassword: string;
  emailVerificationFlow?: EmailVerificationFlow;
  newEmail: string;
}) {
  const currentUser = auth.currentUser;
  const currentEmail = currentUser?.email?.trim();
  const normalizedNewEmail = newEmail.trim();

  if (!currentUser || !currentEmail) {
    throw new Error('You must be signed in with an email account.');
  }

  if (!normalizedNewEmail) {
    throw new Error('Enter the new email address.');
  }

  const credential = EmailAuthProvider.credential(
    currentEmail,
    currentPassword,
  );

  await reauthenticateWithCredential(currentUser, credential);
  await verifyBeforeUpdateEmail(
    currentUser,
    normalizedNewEmail,
    authActionCodeSettings({ emailVerificationFlow }),
  );
}

export async function syncCurrentAuthEmail() {
  const result = await syncCurrentAuthEmailCallable({});
  return result.data;
}

export async function sendPasswordReset(email: string) {
  await sendPasswordResetEmail(auth, email.trim());
}

export async function signOut() {
  await firebaseSignOut(auth);
}
